import * as React from 'react';
import thousands from 'thousands';
import { TextInputForPrint } from 'sos-common/js/components/FormControls/TextInputForPrint';
import { Field } from '../../StatsbidragGlobals/statsbidrag.types';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import {
  calculationComponentProps,
  calculationComponentState,
  calculationComponentModel,
  calculationComponentModel2,
  ColumnTypeGroup,
  ColumnValue,
} from '../calculationComponents.types';
import {
  serializeComponent,
  updateField,
  getAsNumber,
  getInitialFieldsFromJsonData,
  valueObject,
  uuidv4,
  getSerializedXmlInputComponent,
  IJsonDataStuctures,
} from '../calculationComponents.helpers';
import EventService from '../calculationComponents.eventservice';
import { InputError } from 'sos-common/js/components/InputError/InputError';
import ValidationService from '../../../services/validation.service';
import { hashCode } from 'sos-common/js/utils/hash';
import './_membersbysex.scss';

export interface MembersBySexProps extends calculationComponentProps {
  summaryCellId: string;
  groupOneHeading: string;
  groupOneLabelOne: string;
  groupOnePlaceholderOne: string;
  groupOneLabelTwo: string;
  groupOnePlaceholderTwo: string;
  groupOneLabelThree: string;
  groupOnePlaceholderThree: string;
  groupTwoHeading: string;
  groupTwoLabelOne: string;
  groupTwoPlaceholderOne: string;
  groupTwoLabelTwo: string;
  groupTwoPlaceholderTwo: string;
  groupTwoLabelThree: string;
  groupTwoPlaceholderThree: string;
  groupThreeHeading: string;
  groupThreePlaceholderOne: string;
  groupFourHeading: string;
  groupFourPlaceholderOne: string;
  sumHeading: string;
}

export interface MembersBySexState extends calculationComponentState {
  fields: Field[][];
  sum: number;
  errorMessage: string;
  jsonXmlInput: string;
}

export class MembersBySex extends React.Component<MembersBySexProps, MembersBySexState> {
  private _fieldCount: number;
  private _errorId: string;
  private _sumLabel: string = 'Totalt';

  private _getSecondGroupLabel = (value: number) => `Av dessa ${thousands(value, ' ')} betalande medlemmar: `;

  constructor(props: MembersBySexProps) {
    super(props);
    this._fieldCount = 0;
    this._errorId = hashCode(props.summaryCellId, 'error');

    let initialFields: Field[][] = [this.getFieldRow()];

    let isValid = true;

    if (props.formElementJson) {
      try {
        let calculationComponentModel = JSON.parse(props.formElementJson) as calculationComponentModel;
        initialFields = getInitialFieldsFromJsonData(calculationComponentModel, initialFields, this.getFieldRow);

        let validation = ValidationService.validateComponent(initialFields, props.formElementName);
        isValid = validation.componentIsValid;
        initialFields = validation.fieldsClone;
      } catch (error) {
        console.warn('failed to parse JSON:', error);
      }
    }

    this.state = {
      jsonFields: '',
      fields: initialFields,
      sum: 0,
      isValid,
      errorMessage: '',
      isTouched: false,
      jsonXmlInput: '',
    };
  }

  componentDidMount() {
    this.summarizeFields();
    EventService.subscribe('form-is-valid', (e: CustomEvent) => {
      this.setState({
        isTouched: true,
      });
    });
  }

  getSerializedComponent = (fieldmatrix: Field[][]) => {
    const {
      groupOneHeading,
      groupTwoHeading,
      groupThreeHeading,
      formElementHeading,
      sumHeading,
      groupOneLabelOne,
      groupOneLabelTwo,
      groupOneLabelThree,
    } = this.props;

    let values = [];
    fieldmatrix.forEach((fields) => {
      values.push([]);
      fields.forEach((field, i) => {
        if (i !== 3 && i !== 7) {
          values[values.length - 1].push(valueObject(field.value, true));
        } else {
          values[values.length - 1].push(valueObject(field.value, false));
        }
      });
    });
    if (values.length > 0) {
      return serializeComponent(
        [groupOneHeading, groupTwoHeading, groupThreeHeading],
        values,
        [sumHeading, thousands(this.state.sum, ' ')],
        formElementHeading,
        [
          groupOneLabelOne,
          groupOneLabelTwo,
          groupOneLabelThree,
          null,
          groupOneLabelOne,
          groupOneLabelTwo,
          groupOneLabelThree,
          null,
          null,
          null,
        ]
      );
    }
    return '';
  };

  getFieldRow = () => {
    const {
      groupOneLabelOne,
      groupOneLabelTwo,
      groupOneLabelThree,
      groupOnePlaceholderOne,
      groupOnePlaceholderTwo,
      groupOnePlaceholderThree,
      groupTwoLabelOne,
      groupTwoLabelTwo,
      groupTwoLabelThree,
      groupTwoPlaceholderOne,
      groupTwoPlaceholderTwo,
      groupTwoPlaceholderThree,
      groupThreePlaceholderOne,
      groupFourPlaceholderOne,
      groupThreeHeading,
      groupFourHeading,
    } = this.props;
    const id1 = `memberbysex__${this._fieldCount++}${this.props.formElementName}`;
    const id2 = `memberbysex__${this._fieldCount++}${this.props.formElementName}`;
    const id3 = `memberbysex__${this._fieldCount++}${this.props.formElementName}`;
    const id4 = `memberbysex__${this._fieldCount++}${this.props.formElementName}`;
    const id5 = `memberbysex__${this._fieldCount++}${this.props.formElementName}`;
    const id6 = `memberbysex__${this._fieldCount++}${this.props.formElementName}`;
    const id7 = `memberbysex__${this._fieldCount++}${this.props.formElementName}`;
    const id8 = `memberbysex__${this._fieldCount++}${this.props.formElementName}`;
    const id9 = `memberbysex__${this._fieldCount++}${this.props.formElementName}`;
    const id10 = `memberbysex__${this._fieldCount++}${this.props.formElementName}`;
    return [
      {
        id: id1,
        name: id1,
        value: '0',
        label: groupOneLabelOne,
        placeholder: groupOnePlaceholderOne,
        isValid: true,
        mandatory: true,
        actAsNumber: true,
        isTouched: false,
        errorMessage: 'Du måste ange ' + (groupOneLabelOne ? groupOneLabelOne.toLowerCase() : 'denna uppgift'),
      },
      {
        id: id2,
        name: id2,
        value: '0',
        label: groupOneLabelTwo,
        placeholder: groupOnePlaceholderTwo,
        isValid: true,
        mandatory: true,
        actAsNumber: true,
        isTouched: false,
        errorMessage: 'Du måste ange ' + (groupOneLabelTwo ? groupOneLabelTwo.toLowerCase() : 'denna uppgift'),
      },
      {
        id: id3,
        name: id3,
        value: '0',
        label: groupOneLabelThree,
        placeholder: groupOnePlaceholderThree,
        isValid: true,
        mandatory: true,
        actAsNumber: true,
        isTouched: false,
        errorMessage: 'Du måste ange ' + (groupOneLabelThree ? groupOneLabelThree.toLowerCase() : 'denna uppgift'),
      },
      {
        id: id4,
        name: id4,
        value: '0',
        isValid: true,
        mandatory: true,
        actAsNumber: true,
      },
      {
        id: id5,
        name: id5,
        value: '0',
        label: groupTwoLabelOne,
        placeholder: groupTwoPlaceholderOne,
        isValid: true,
        mandatory: true,
        actAsNumber: true,
        isTouched: false,
        errorMessage: 'Du måste ange ' + (groupTwoLabelOne ? groupTwoLabelOne.toLowerCase() : 'denna uppgift'),
      },
      {
        id: id6,
        name: id6,
        value: '0',
        label: groupTwoLabelTwo,
        placeholder: groupTwoPlaceholderTwo,
        isValid: true,
        mandatory: true,
        actAsNumber: true,
        isTouched: false,
        errorMessage: 'Du måste ange ' + (groupTwoLabelTwo ? groupTwoLabelTwo.toLowerCase() : 'denna uppgift'),
      },
      {
        id: id7,
        name: id7,
        value: '0',
        label: groupTwoLabelThree,
        placeholder: groupTwoPlaceholderThree,
        isValid: true,
        mandatory: true,
        actAsNumber: true,
        isTouched: false,
        errorMessage: 'Du måste ange ' + (groupTwoLabelThree ? groupTwoLabelThree.toLowerCase() : 'denna uppgift'),
      },
      {
        id: id8,
        name: id8,
        value: '0',
        isValid: true,
        mandatory: true,
        actAsNumber: true,
      },
      {
        id: id9,
        name: id9,
        value: '0',
        placeholder: groupThreePlaceholderOne,
        isValid: true,
        mandatory: true,
        actAsNumber: true,
        isTouched: false,
        errorMessage: 'Du måste ange ' + (groupThreeHeading ? groupThreeHeading.toLowerCase() : 'denna uppgift'),
      },
      {
        id: id10,
        name: id10,
        value: '0',
        placeholder: groupFourPlaceholderOne,
        isValid: true,
        mandatory: true,
        actAsNumber: true,
        isTouched: false,
        errorMessage: 'Du måste ange ' + (groupFourHeading ? groupFourHeading.toLowerCase() : 'denna uppgift'),
      },
    ] as Field[];
  };

  summarizeFields = () => {
    const { formElementName, formElementHeading } = this.props;
    const fieldsClone = this.state.fields.slice();

    let group1Sum = 0;
    let group2Sum = 0;

    fieldsClone[0].forEach((field, i) => {
      if (i < 3) group1Sum += getAsNumber(field.value);
      if (i > 3 && i < 7) group2Sum += getAsNumber(field.value);
    });

    fieldsClone[0][3].value = group1Sum;
    fieldsClone[0][7].value = group2Sum;

    this.setState(
      {
        fields: fieldsClone,
        sum: group1Sum,
      },
      () => {
        this.validateMe();
        EventService.fire(`cell-updated:${this.props.summaryCellId}`, { detail: this.state.sum });
        this.setState({
          jsonFields: this.getSerializedComponent(this.state.fields),
          jsonXmlInput: getSerializedXmlInputComponent(
            this.state.fields,
            calculationComponentModel2.Create(formElementName, formElementHeading, 'Könsfördelning föreningsmedlemmar'),
            this.jsonSeriliazetoMainContentForXmlInput
          ),
        });
      }
    );
  };

  jsonSeriliazetoMainContentForXmlInput = (fields: Field[][], data: IJsonDataStuctures) => {
    if (fields.length !== 1) throw 'For this component MembersBySex, there should only be 1 Field';
    const field = fields[0];
    if (field.length !== 10) throw 'For this component MembersBySex, there should only be exactly 9 inner Fields';

    const { groupOneHeading, groupTwoHeading, groupThreeHeading, groupFourHeading } = this.props;

    let columnValues = [] as ColumnValue[];
    for (let i = 0; i < 3; i++) {
      columnValues.push({ headerValue: field[i].label, value: field[i].value });
    }
    const sumValue = field[3].value;
    columnValues.push({ headerValue: this._sumLabel, value: sumValue, isSum: true });
    const firstGroup = { headerValue: groupOneHeading, columnValues } as ColumnTypeGroup;
    data.columnTypeGroups.push(firstGroup);

    columnValues = [] as ColumnValue[];
    for (let i = 4; i < 7; i++) {
      columnValues.push({ headerValue: field[i].label, value: field[i].value });
    }
    columnValues.push({ headerValue: groupThreeHeading, value: field[8].value });
    columnValues.push({ headerValue: groupFourHeading, value: field[9].value });

    const headerValue = this._getSecondGroupLabel(sumValue) + groupTwoHeading;
    const secondGroup = { headerValue, columnValues } as ColumnTypeGroup;
    data.columnTypeGroups.push(secondGroup);
  };

  validateMe = () => {
    let isValid = true;
    let errorMessage = '';

    // Validate standard field logic
    let validation = ValidationService.validateComponent(this.state.fields, this.props.formElementName);
    isValid = validation.componentIsValid;
    let fields = validation.fieldsClone;
    //if (!isValid) errorMessage = ALL_MANDATORY;

    // Validate business logic
    if (
      this.state.fields[0][7].value +
        getAsNumber(this.state.fields[0][8].value) +
        getAsNumber(this.state.fields[0][9].value) !==
      this.state.fields[0][3].value
    ) {
      isValid = false;
      errorMessage = `Detaljinformationen om antal medlemmar måste stämma med det totala antalet medlemmar (${this.state.fields[0][3].value})`;
    }

    this.setState(
      {
        fields,
        isValid,
        errorMessage,
      },
      () => {
        ValidationService.validateStepViaElementName(this.props.formElementName);
      }
    );
  };

  setValue = (field: FormField<any>) => {
    const fieldsClone = this.state.fields.slice();
    const fieldToUpdate = updateField(field, fieldsClone);

    this.setState(
      {
        fields: fieldsClone,
        //isTouched: true
      },
      () => {
        EventService.fire(`cell-updated:${fieldToUpdate.id}`, { detail: fieldToUpdate.value });
        this.summarizeFields();
      }
    );
  };

  renderRows = (field: Field[], index: number) => {
    const { groupOneHeading, groupTwoHeading, groupThreeHeading, groupFourHeading } = this.props;
    this.setAriaErrorObjects(field);

    return (
      <div key={`memberbysexgroup_${index}`} className="salaryexp__group form-group-small">
        <h3 className="like-a-label">{groupOneHeading}</h3>
        <div className="indent m-b-4">
          <div className="form-group-small">
            <label htmlFor={field[0].id}>{field[0].label}</label>
            <TextInputForPrint
              id={field[0].id}
              classNames={this.shouldShowError(field[0]) ? 'has-error' : ''}
              placeholder={field[0].placeholder}
              value={field[0].value}
              onChange={this.setValue}
              fieldName={field[0].name}
              type={field[0].type}
              actAsNumber={field[0].actAsNumber}
              isRequired={field[0].mandatory}
              onBlur={this.onBlur}
              errorAriaAttributes={field[0].errorAriaAttributes}
            />
            {this.renderError(field[0])}
          </div>

          <div className="form-group-small">
            <label htmlFor={field[1].id}>{field[1].label}</label>
            <TextInputForPrint
              id={field[1].id}
              classNames={this.shouldShowError(field[1]) ? 'has-error' : ''}
              placeholder={field[1].placeholder}
              value={field[1].value}
              onChange={this.setValue}
              fieldName={field[1].name}
              type={field[1].type}
              actAsNumber={field[1].actAsNumber}
              isRequired={field[1].mandatory}
              onBlur={this.onBlur}
              errorAriaAttributes={field[1].errorAriaAttributes}
            />
            {this.renderError(field[1])}
          </div>

          <div className="form-group-small">
            <label htmlFor={field[2].id}>{field[2].label}</label>
            <TextInputForPrint
              id={field[2].id}
              classNames={this.shouldShowError(field[2]) ? 'has-error' : ''}
              placeholder={field[2].placeholder}
              value={field[2].value}
              onChange={this.setValue}
              fieldName={field[2].name}
              type={field[2].type}
              actAsNumber={field[2].actAsNumber}
              isRequired={field[2].mandatory}
              onBlur={this.onBlur}
              errorAriaAttributes={field[2].errorAriaAttributes}
            />
            {this.renderError(field[2])}
          </div>
          <div className="autosum__summary">
            <div className="autosum__col1">
              <span className="like-a-label">{this._sumLabel}</span>
            </div>
            <div className="autosum__col2">
              <input
                className="like-a-label autosum__sumfield"
                id={field[3].id}
                readOnly={true}
                type="text"
                value={thousands(field[3].value, ' ')}
              />
            </div>
          </div>
        </div>

        <div className="membersbysex__whitebox">
          <h2 className="h3">{this._getSecondGroupLabel(field[3].value)}</h2>
          <div className="indent m-b-4">
            <h3 className="like-a-label">{groupTwoHeading}</h3>
            <div className="form-group-small">
              <label htmlFor={field[4].id}>{field[4].label}</label>
              <TextInputForPrint
                id={field[4].id}
                classNames={this.shouldShowError(field[4]) ? 'has-error' : ''}
                placeholder={field[4].placeholder}
                value={field[4].value}
                onChange={this.setValue}
                fieldName={field[4].name}
                type={field[4].type}
                actAsNumber={field[4].actAsNumber}
                isRequired={field[4].mandatory}
                onBlur={this.onBlur}
                errorAriaAttributes={field[4].errorAriaAttributes}
              />
              {this.renderError(field[4])}
            </div>

            <div className="form-group-small">
              <label htmlFor={field[5].id}>{field[5].label}</label>
              <TextInputForPrint
                id={field[5].id}
                classNames={this.shouldShowError(field[5]) ? 'has-error' : ''}
                placeholder={field[5].placeholder}
                value={field[5].value}
                onChange={this.setValue}
                fieldName={field[5].name}
                type={field[5].type}
                actAsNumber={field[5].actAsNumber}
                isRequired={field[5].mandatory}
                onBlur={this.onBlur}
                errorAriaAttributes={field[5].errorAriaAttributes}
              />
              {this.renderError(field[5])}
            </div>

            <div className="form-group-small">
              <label htmlFor={field[6].id}>{field[6].label}</label>
              <TextInputForPrint
                id={field[6].id}
                classNames={this.shouldShowError(field[6]) ? 'has-error' : ''}
                placeholder={field[6].placeholder}
                value={field[6].value}
                onChange={this.setValue}
                fieldName={field[6].name}
                type={field[6].type}
                actAsNumber={field[6].actAsNumber}
                isRequired={field[6].mandatory}
                onBlur={this.onBlur}
                errorAriaAttributes={field[6].errorAriaAttributes}
              />
              {this.renderError(field[6])}
            </div>

            <div className="form-group-small">
              <label htmlFor={field[8].id}>{groupThreeHeading}</label>
              <TextInputForPrint
                id={field[8].id}
                classNames={this.shouldShowError(field[8]) ? 'has-error' : ''}
                placeholder={field[8].placeholder}
                value={field[8].value}
                onChange={this.setValue}
                fieldName={field[8].name}
                type={field[8].type}
                actAsNumber={field[8].actAsNumber}
                isRequired={field[8].mandatory}
                onBlur={this.onBlur}
                errorAriaAttributes={field[8].errorAriaAttributes}
              />
              {this.renderError(field[8])}
            </div>

            <div className="form-group-small">
              <label htmlFor={field[9].id}>{groupFourHeading}</label>
              <TextInputForPrint
                id={field[9].id}
                classNames={this.shouldShowError(field[9]) ? 'has-error' : ''}
                placeholder={field[9].placeholder}
                value={field[9].value}
                onChange={this.setValue}
                fieldName={field[9].name}
                type={field[9].type}
                actAsNumber={field[9].actAsNumber}
                isRequired={field[9].mandatory}
                onBlur={this.onBlur}
                errorAriaAttributes={field[9].errorAriaAttributes}
              />
              {this.renderError(field[9])}
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderError = (field: Field) => {
    if (this.shouldShowError(field) && field.errorAriaAttributes) {
      return (
        <InputError
          key={`${field.id}_cError`}
          id={field.errorAriaAttributes['aria-describedby']}
          errorText={field.errorMessage}
        />
      );
    }
  };

  setAriaErrorObjects = (fieldRow: Field[]) => {
    fieldRow.forEach((field) => {
      field.errorAriaAttributes = null;
      if (this.shouldShowError(field)) {
        field.errorAriaAttributes = { 'aria-invalid': true, 'aria-describedby': uuidv4() };
      }
    });
  };

  shouldShowError = (field: Field) => {
    return (this.state.isTouched || field.isTouched) && !field.isValid;
  };

  onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { fields } = this.state;
    const clone = fields.slice();
    let targetField: Field = null;
    clone.forEach((field) => {
      let foundField = field.find((f) => f.id === e.target.id);
      if (foundField) targetField = foundField;
    });
    if (targetField) {
      targetField.isTouched = true;
      this.setState({ fields: clone });
    }
  };

  public render() {
    const { summaryCellId, sumHeading } = this.props;
    const { fields, sum, isValid, errorMessage } = this.state;
    return (
      <div className="salaryexp cc-component">
        {fields && fields.map(this.renderRows)}

        {errorMessage && <InputError id={this._errorId} errorText={errorMessage} />}
        <input
          data-isvalid={this.state.isValid}
          type="hidden"
          name={this.props.formElementName}
          id={this.props.formElementName}
          value={this.state.jsonFields}
        />
        <input
          data-isvalid={this.state.isValid}
          type="hidden"
          name={'__XmlInput' + this.props.formElementName}
          id={'__XmlInput' + this.props.formElementName}
          value={this.state.jsonXmlInput}
        />
      </div>
    );
  }
}
